import React, { forwardRef, useMemo } from 'react';
import styled from 'styled-components';
import { COLOR_SYSTEM } from '../../design/design-system';
import { AIReview, Drug, DrugSearchResult } from '../../../common/models/types';
import PatientInfoDisplay from '../../molecules/PatientInfoDisplay';
import ReviewSection from '../AIReviewDetailTemplate/ReviewSection';
import { buildHospitalAliasMapFromGroups } from '../../../utils/extractDepartment';
import { PrintOptions } from './index';
import { MentionedAddition } from '../../../utils/prescriptionPrintScope';
import PrescriptionScopeSection from './PrescriptionScopeSection';
import { drugCardSizeCss, DrugCardSize } from './drugCardSizeCss';

const ContentContainer = styled.div<{ $cardSize: DrugCardSize }>`
  width: 210mm; /* A4 width */
  min-width: 210mm; /* A4 최소 너비 - 브라우저 창 크기와 무관하게 유지 */
  max-width: 210mm; /* A4 최대 너비 */
  margin: 0 auto;
  padding: 20mm;
  background: white;
  min-height: 100vh;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);

  ${({ $cardSize }) => drugCardSizeCss($cardSize)}

  @media screen {
    margin: 0 auto;
  }

  @media print {
    padding: 0;  /* @page 마진으로 대체 (중복 방지) */
    max-width: 100%;
    width: 100%;
    min-width: initial;
    box-shadow: none;
    margin: 0;
    min-height: initial !important;
    height: auto !important;
    overflow: visible !important;
    display: block !important;
    position: relative !important;
  }
`;

const Header = styled.div`
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid ${COLOR_SYSTEM.get("Gray")?.[300]};

  @media print {
    page-break-after: avoid;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  margin: 0;
`;

const Section = styled.div<{ $pageBreak?: boolean; $isFirst?: boolean }>`
  margin-bottom: 32px;

  @media print {
    page-break-after: auto;
    /* 첫 번째 Section은 내부에서 페이지 나눔 허용 (Header와 함께 한 페이지에 들어가지 않을 수 있음) */
    page-break-inside: ${props => props.$isFirst ? 'auto' : 'avoid'};
    ${props => props.$pageBreak && `
      page-break-before: always;
      break-before: page;
    `}
  }
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
`;

const ReviewSectionWrapper = styled.div`
  margin-bottom: 32px;

  @media print {
    page-break-before: auto;
    page-break-after: auto;
    page-break-inside: auto;
  }
`;

interface PrintContentProps {
  review: AIReview;
  patient?: any;
  age: string;
  printOptions: PrintOptions;
  drugsData?: Drug[];
  pillsData?: DrugSearchResult[];
  /** "보고서 언급으로 추가될 약" 목록(품목당 최신 처방 1건) — 참고 표에 표시 */
  mentionedAdditions?: MentionedAddition[];
}

const PrintContent = forwardRef<HTMLDivElement, PrintContentProps>(
  ({ review, patient, age, printOptions, drugsData, pillsData, mentionedAdditions = [] }, ref) => {
    // 날짜 포맷팅
    const formatDate = (dateString?: string) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    };

    // 편집된 버전이 있는지 확인
    const hasEditedVersion = !!review.reviewResultEdited;
    const reviewContent = hasEditedVersion
      ? review.reviewResultEdited
      : review.reviewResult;

    // 병원 실명 복원 맵. onHospitalClick 미전달이므로 버튼이 아닌 실명 평문으로 복원된다.
    // ⚠️ 서한 수신 의사에게 타 기관 실명이 노출되는 것은 의도된 변경(spec 설계 결정 8).
    const hospitalAliasMap = buildHospitalAliasMapFromGroups(patient?.prescriptionGroups);

    /**
     * 첫 번째로 렌더링되는 Section 판단 (페이지 나눔 제어용)
     *
     * 배경:
     * - 출력 옵션에 따라 환자 정보와 처방 정보는 숨길 수 있음
     * - 각 Section에 page-break를 고정값으로 주면, 첫 Section이 숨겨졌을 때 불필요한 빈 페이지 생성
     *
     * 해결:
     * - 조건부 렌더링되는 순서를 고려하여 첫 번째 Section만 페이지 나눔 제외
     * - 나머지 Section은 새 페이지에서 시작
     *
     * 예시:
     * - 환자 정보 표시 O → 'patient' 반환 → 환자 정보만 페이지 나눔 없음
     * - 환자 정보 숨김 + 처방 정보 표시 O → 'prescription' 반환 → 처방 정보만 페이지 나눔 없음
     * - 둘 다 숨김 → 'analysis' 반환 → 분석 결과만 페이지 나눔 없음
     */
    const firstVisibleSection = useMemo(() => {
      // 환자 정보가 표시되면 첫 Section
      if (printOptions.showPatientInfo) {
        return 'patient';
      }

      // 환자 정보 숨김 + 처방 정보가 표시되고 실제 데이터가 있으면 첫 Section
      if (printOptions.showPrescriptionTable && patient &&
          (patient.prescriptionGroups?.length > 0 ||
           patient.otc?.length > 0 ||
           patient.hff?.length > 0)) {
        return 'prescription';
      }

      // 환자 정보, 처방 정보 둘 다 숨기면 분석 결과가 첫 Section
      return 'analysis';
    }, [printOptions.showPatientInfo, printOptions.showPrescriptionTable, patient]);

    const shouldRenderPatient = printOptions.showPatientInfo;
    // 섹션 표시 여부는 "원본" 데이터 기준 — 필터 결과가 0건이어도 섹션을 유지하고
    // 빈 상태 문구로 설명한다 (섹션이 사라지면 지면에 이유가 안 남음. 설계 §6.3)
    const shouldRenderPrescription = printOptions.showPrescriptionTable && patient && (
      patient.prescriptionGroups?.length > 0 || patient.otc?.length > 0 || patient.hff?.length > 0
    );

    // 분석 결과 렌더링 여부 판단
    // - 콘텐츠에 섹션 마커([약사용 보고서], [환자용 복약지도문])가 없으면 항상 표시
    // - 섹션 마커가 있으면 출력 옵션에 따라 표시 여부 결정 (둘 다 숨기면 안 보임)
    const hasPharmacistReport = reviewContent?.includes('[약사용 보고서]');
    const hasPatientGuide = reviewContent?.includes('[환자용 복약지도문]')
      || reviewContent?.includes('[환자용 복약지도]')
      || reviewContent?.includes('[종사자용 안내문]');
    const hasSectionMarkers = hasPharmacistReport || hasPatientGuide;

    const shouldRenderAnalysis = hasSectionMarkers
      ? (!printOptions.hidePharmacistReport || !printOptions.hidePatientGuide)
      : true;

    return (
      <ContentContainer ref={ref} $cardSize={printOptions.patientGuideCardSize}>
        <Header>
          <Title>약물요법 검토보고서</Title>
          <Subtitle>
            생성일: {formatDate(review.createdAt ? String(review.createdAt) : undefined)} |
            리뷰 ID: {review.id}
          </Subtitle>
        </Header>

        {/*
          환자 정보 Section (조건부 렌더링)
          - 출력 옵션에서 숨길 수 있음
          - 페이지 나눔: 첫 번째 Section이 아닐 때만 적용
          - $isFirst: 첫 번째 Section은 page-break-inside: auto로 설정하여 Header와 함께 페이지를 초과할 경우 내부에서 나눔 허용
        */}
        {shouldRenderPatient && (
          <Section
            data-print-anchor="patient"
            $pageBreak={firstVisibleSection !== 'patient'}
            $isFirst={firstVisibleSection === 'patient'}
          >
            <SectionTitle>환자 정보</SectionTitle>
            <PatientInfoDisplay
              patient={patient}
              age={age}
              promptType={review.promptType}
            />
          </Section>
        )}

        {/*
          처방 정보 Section (조건부 렌더링)
          - 출력 옵션에서 숨길 수 있음
          - 처방 데이터가 실제로 있을 때만 표시 (prescriptionGroups, otc, hff 중 하나라도 존재)
          - 범위 필터·병합·캡션·소표·범례는 PrescriptionScopeSection이 담당 (탑재뷰와 공유)
        */}
        {shouldRenderPrescription && (
          <Section
            data-print-anchor="prescription"
            $pageBreak={firstVisibleSection !== 'prescription'}
            $isFirst={firstVisibleSection === 'prescription'}
          >
            <SectionTitle>복용약 정보</SectionTitle>
            <PrescriptionScopeSection
              prescriptionGroups={patient.prescriptionGroups || []}
              otc={patient.otc || []}
              hff={patient.hff || []}
              printOptions={printOptions}
              drugsData={drugsData}
              mentionedAdditions={mentionedAdditions}
            />
          </Section>
        )}

        {/*
          분석 결과 Section (조건부 렌더링)
          - 섹션 마커가 있고 둘 다 숨겨진 경우 전체 Section을 표시하지 않음
          - 페이지 나눔: 첫 번째 Section이 아닐 때만 적용
          - $isFirst: 첫 번째 Section은 page-break-inside: auto로 설정하여 Header와 함께 페이지를 초과할 경우 내부에서 나눔 허용
        */}
        {shouldRenderAnalysis && (
          <Section
            data-print-anchor="analysis"
            $pageBreak={firstVisibleSection !== 'analysis'}
            $isFirst={firstVisibleSection === 'analysis'}
          >
            <ReviewSectionWrapper>
              <ReviewSection
                reviewResult={review.reviewResult}
                reviewResultEdited={review.reviewResultEdited}
                status={review.status}
                message={""}
                promptType={review.promptType}
                isEditMode={false}
                editedContent=""
                showEditedVersion={hasEditedVersion}
                onEnterEditMode={() => {}}
                onExitEditMode={() => {}}
                onContentChange={() => {}}
                onSave={() => {}}
                onDelete={() => {}}
                onReferenceClick={() => {}}
                referenceMode={printOptions.referenceMode}
                drugsData={drugsData}
                pillsData={pillsData}
                sectionVisibility={{
                  hidePharmacistReport: printOptions.hidePharmacistReport,
                  hidePatientGuide: printOptions.hidePatientGuide
                }}
                hospitalAliasMap={hospitalAliasMap}
              />
            </ReviewSectionWrapper>
          </Section>
        )}

      </ContentContainer>
    );
  }
);

PrintContent.displayName = 'PrintContent';

export default PrintContent;
